import { StatCard, StatCardTypography } from "Components/UI/StatCard";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import useChartData from "features/analytics/hooks/useChartData";
import { IAnalyticsStatCardProps } from "features/analytics/types/types";
import { ApiChartTemplate, ApiDimension, ApiMetric } from "@incendium/api";
import { useCallback } from "react";
import { uniqueMetricsFromChart } from "features/analytics/utils/utils";
import { useChartDescriptionDrawer } from "Providers/ChartDescriptionDrawerProvider";

function AnalyticsStatCard({
  chart,
  renderBody,
  setDirection,
  chartOutput: propChartOutput,
  includeTrendedSpark,
  noBg,
  forceComparison,
  overrideComparisonOutput,
  overrideData,
  overrideComparisonData,
  loading: externalLoading,
  icon,
  ...rest
}: IAnalyticsStatCardProps) {
  const {
    chartOutput,
    chartComparisonOutput,
    overrideChartComparisonOutput: overrideChartComparisonOutputProp,
    calcNumberOfDays,
  } = useFromToContext();
  const { openDrawer } = useChartDescriptionDrawer();
  const { selectedProject } = useSelectedProject();

  const overrideChartComparisonOutput =
    overrideComparisonOutput || overrideChartComparisonOutputProp;

  const skipFetch = overrideData !== undefined;

  const { chartData, loading, comparisonChartData } = useChartData(
    selectedProject,
    {
      ...chart,
      template: ApiChartTemplate.SNAPSHOT,
    },
    skipFetch ? null : propChartOutput || chartOutput,
    {
      comparisonFrom: forceComparison
        ? overrideChartComparisonOutput.from
        : chartComparisonOutput?.from,
      comparisonTo: forceComparison
        ? overrideChartComparisonOutput.to
        : chartComparisonOutput?.to,
    },
  );

  const { chartData: trendedChartData, loading: trendedLoading } = useChartData(
    selectedProject,
    {
      ...chart,
      dimension: [
        calcNumberOfDays(propChartOutput || chartOutput) > 200
          ? ApiDimension.DIMENSION_SESSION_START_BY_WEEK
          : ApiDimension.DIMENSION_SESSION_START_BY_DAY,
      ],
      template: ApiChartTemplate.TREND,
    },
    includeTrendedSpark && !skipFetch ? propChartOutput || chartOutput : null,
  );

  const onDescribe = useCallback(() => {
    const metrics = uniqueMetricsFromChart(chart);
    openDrawer(
      (chart.dimension || []) as ApiDimension[],
      metrics as ApiMetric[],
      chart.name && chart.name !== "" ? `${chart.name} Metrics` : undefined,
    );
  }, [chart, openDrawer]);

  const resolvedData = overrideData ?? chartData?.data;
  const resolvedComparisonData =
    overrideComparisonData ?? comparisonChartData?.data;

  return (
    <StatCard
      bgIndex={chart.displayOptions?.statDesignIndex}
      noBg={noBg}
      loading={skipFetch ? externalLoading : loading || trendedLoading}
      onDescribe={onDescribe}
      icon={icon}
      direction={
        setDirection && resolvedData
          ? setDirection(resolvedData, resolvedComparisonData)
          : rest.direction
      }
      {...rest}
    >
      {resolvedData && resolvedData.length > 0 ? (
        renderBody(resolvedData, resolvedComparisonData, trendedChartData?.data)
      ) : (
        <StatCardTypography size="small">No Data</StatCardTypography>
      )}
    </StatCard>
  );
}

export default AnalyticsStatCard;
